import {
  FeatureEntitlementName,
  UsageLimitEntitlementName,
} from '@jupiterone/entitlement-types';
import { JupiterTheme } from '@jupiterone/jupiter-web-toolkit';
import { Tooltip, TooltipProps } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';
import React from 'react';
import { useEntitlements } from '@jupiterone/jupiter-web-toolkit/src/entitlements/useEntitlements';
import { useUsageLimitEntitlementAccessQuery } from '@jupiterone/jupiter-web-toolkit/src/entitlements/useUsageLimitEntitlementAccess';
import clsx from 'clsx';
import { EntitlementsPrompt } from './EntitlementsUpgradePrompt';

const useStyles = makeStyles((theme: JupiterTheme) =>
  createStyles({
    tooltip: {
      padding: theme.spacing(1.25),
    },
    childrenContainer: { display: 'inline-block', alignSelf: 'flex-start' },
  })
);

export const FEATURE_ENTITLEMENTS_TOOLTIPS: Record<
  FeatureEntitlementName,
  string
> = {
  [FeatureEntitlementName.POLICIES_EXPORT]: 'policies downloads',
  [FeatureEntitlementName.QUESTION_TREND_ANALYSIS]: 'questions trend analysis',
  [FeatureEntitlementName.API_ACCESS]: 'api access',
  [FeatureEntitlementName.SAML_SSO]: 'SSO access',
  [FeatureEntitlementName.POLICIES_ADD_CUSTOM]: 'adding policies',
  [FeatureEntitlementName.RBAC_AND_QUERY_PERMISSIONS]: 'access control',
  [FeatureEntitlementName.ALERTS]: 'alerts access',
  [FeatureEntitlementName.CLOUDTRAIL_EVENTS]: 'cloudtrail events',
  [FeatureEntitlementName.COMPLIANCE]: 'compliance access',
  [FeatureEntitlementName.GRAPH_VIEWER]: 'Graph Viewer access',
  [FeatureEntitlementName.INSIGHTS]: 'Insights access',
  [FeatureEntitlementName.INTEGRATIONS]: 'Integrations',
  [FeatureEntitlementName.INVENTORY]: 'Asset Inventory access',
  [FeatureEntitlementName.LANDING]: 'Landing access',
  [FeatureEntitlementName.MY_SECURITY]: 'My Security access',
  [FeatureEntitlementName.POLICIES]: 'Policies and Procedures access',
  [FeatureEntitlementName.POWER_UPS]: 'Powerups',
  [FeatureEntitlementName.STREAMED_ALERT_EVALUATION]:
    'streamed alert evaluations',
  [FeatureEntitlementName.VANITY_DOMAIN_URL]: 'a vanity domain url',
  [FeatureEntitlementName.VULNERABILITY_MANAGEMENT]: 'vulnerability management',
  [FeatureEntitlementName.CUSTOM_MAPPING_RULES]: 'custom mapping rules',
  [FeatureEntitlementName.INSIGHTS_EXTERNAL_SHARING]:
    'insights external sharing',
  [FeatureEntitlementName.CUSTOM_LOGO]: 'custom logo',
  [FeatureEntitlementName.COMPLIANCE_EXTERNAL_SHARING]:
    'compliance external sharing',
};

export const createFeatureEntitlementsPrompt = (featureText: string) =>
  `Your current JupiterOne plan doesn't include ${featureText}.`;

export interface EntitlementsTooltipProps
  extends Pick<
    TooltipProps,
    Exclude<keyof TooltipProps, 'children' | 'title'>
  > {
  hasEntitlement: boolean;
  prompt?: string;
  fallbackTitle?: string;
  childrenContainerClass?: string;
  children: (hasEntitlement: boolean) => any;
  loadingEntitlement?: boolean;
}

// controlled entitlement tooltip
export const EntitlementsTooltip = ({
  children,
  prompt = '',
  hasEntitlement,
  fallbackTitle = '',
  childrenContainerClass = '',
  loadingEntitlement = false,
  ...props
}: EntitlementsTooltipProps) => {
  const classes = useStyles();
  const hasChildrenContainerClass =
    childrenContainerClass && childrenContainerClass !== '';

  return (
    <Tooltip
      {...props}
      classes={{ tooltip: hasEntitlement ? undefined : classes.tooltip }}
      title={
        hasEntitlement || loadingEntitlement ? (
          fallbackTitle
        ) : (
          <EntitlementsPrompt prompt={prompt} />
        )
      }
      arrow={!hasEntitlement}
      interactive={!hasEntitlement}
      disableTouchListener={!fallbackTitle && hasEntitlement}
      disableHoverListener={!fallbackTitle && hasEntitlement}
      disableFocusListener={!fallbackTitle && hasEntitlement}
    >
      <div
        className={clsx(
          hasChildrenContainerClass
            ? childrenContainerClass
            : classes.childrenContainer
        )}
      >
        {children(hasEntitlement && !loadingEntitlement)}
      </div>
    </Tooltip>
  );
};

export interface EntitlementsFeatureTooltipProps
  extends Pick<
    TooltipProps,
    Exclude<keyof TooltipProps, 'children' | 'title'>
  > {
  entitlement: FeatureEntitlementName;
  childrenContainerClass?: string;
  children: (hasEntitlement: boolean) => any;
  fallbackTitle?: string;
}

// feature entitlements tooltip and provider
export const EntitlementsFeatureTooltip = ({
  entitlement,
  ...props
}: EntitlementsFeatureTooltipProps) => {
  const { entitlementEnforce, loading } = useEntitlements();
  const hasEntitlement =
    !entitlement || (!loading && entitlementEnforce(entitlement));

  return (
    <EntitlementsTooltip
      hasEntitlement={hasEntitlement}
      prompt={
        entitlement
          ? createFeatureEntitlementsPrompt(
              FEATURE_ENTITLEMENTS_TOOLTIPS[entitlement]
            )
          : undefined
      }
      loadingEntitlement={loading}
      {...props}
    />
  );
};

export const USAGE_ENTITLEMENTS_TOOLTIPS: Record<
  UsageLimitEntitlementName,
  string
> = {
  [UsageLimitEntitlementName.COMPLIANCE_SHARED_FRAMEWORKS]:
    'compliance shared frameworks',
  [UsageLimitEntitlementName.CUSTOM_ALERT_RULES]: 'custom alert rules',
  [UsageLimitEntitlementName.INSIGHTS_WIDGETS_PER_BOARD]:
    'widgets on this board',
  [UsageLimitEntitlementName.INSIGHTS_PERSONAL_DASHBOARDS]:
    'personal dashboards',
  [UsageLimitEntitlementName.INSIGHTS_SHARED_DASHBOARDS]: 'shared dashboards',
  [UsageLimitEntitlementName.INTEGRATION_INSTANCES]: 'integration intances',
  [UsageLimitEntitlementName.ACCOUNT_USERS]: 'account users',
  [UsageLimitEntitlementName.CUSTOM_QUESTIONS]: 'custom questions',
  [UsageLimitEntitlementName.COMPLIANCE_STANDARDS]: 'compliance standards',
  [UsageLimitEntitlementName.COMPLIANCE_EVIDENCE_UPLOAD_GB]:
    'gigabytes of compliance evidence',
};

export const createUsageEntitlementsPrompt = ({
  limitName,
  usage,
  limit,
}: {
  limitName: string;
  usage: number;
  limit: number;
}) =>
  `You have reached your limit (${usage}/${limit}) for the number of ${limitName} you can have in your current JupiterOne plan.`;
export interface EntitlementsUsageTooltipProps
  extends Pick<
    TooltipProps,
    Exclude<keyof TooltipProps, 'children' | 'title'>
  > {
  entitlement: UsageLimitEntitlementName;
  children: (hasEntitlement: boolean) => any;
  fallbackTitle?: string;
  childrenContainerClass?: string;
}

// usage entitlements tooltip and provider
export const EntitlementsUsageTooltip = ({
  entitlement,
  ...props
}: EntitlementsUsageTooltipProps) => {
  const {
    canAccess,
    loading,
    usage,
    limit,
  } = useUsageLimitEntitlementAccessQuery({
    variables: {
      usageLimitEntitlementName: entitlement,
    },
  });

  return (
    <EntitlementsTooltip
      hasEntitlement={canAccess}
      prompt={createUsageEntitlementsPrompt({
        limitName: USAGE_ENTITLEMENTS_TOOLTIPS[entitlement],
        usage,
        limit,
      })}
      loadingEntitlement={loading}
      {...props}
    />
  );
};
